'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

interface MegaMenuSection {
  title: string;
  items: { label: string; href: string; badge?: string }[];
}

const megaMenuSections: MegaMenuSection[] = [
  {
    title: 'Browse Events',
    items: [
      { label: 'All Events', href: '#discover' },
      { label: 'Music & Concerts', href: '#music' },
      { label: 'Tech & Innovation', href: '#tech' },
      { label: 'Food & Drink', href: '#food' },
      { label: 'Sports & Fitness', href: '#sports' },
      { label: 'Arts & Culture', href: '#arts' },
      { label: 'Business & Networking', href: '#business' },
      { label: 'Health & Wellness', href: '#wellness' },
    ],
  },
  {
    title: 'For Organizers',
    items: [
      { label: 'Create an Event', href: '#create', badge: 'FREE' },
      { label: 'Organizer Dashboard', href: '#dashboard' },
      { label: 'Pricing & Fees', href: '#pricing' },
      { label: 'Promote Your Event', href: '#promote' },
      { label: 'Analytics & Reports', href: '#analytics' },
    ],
  },
  {
    title: 'Discover',
    items: [
      { label: 'Events Near Me', href: '#nearby' },
      { label: 'Online Events', href: '#online' },
      { label: 'Free Events', href: '#free' },
      { label: 'This Weekend', href: '#weekend' },
      { label: 'Featured Events', href: '#featured', badge: 'HOT' },
    ],
  },
  {
    title: 'Support',
    items: [
      { label: 'Help Center', href: '#help' },
      { label: 'Contact Us', href: '#contact' },
      { label: 'Buyer Protection', href: '#protection' },
      { label: 'Refund Policy', href: '#refunds' },
    ],
  },
];

const mobileNavItems = [
  { label: 'Events', href: '#events' },
  { label: 'Awards', href: '#awards' },
  { label: 'Attractions', href: '#attractions' },
  { label: 'Exhibitors', href: '#exhibitors' },
  { label: 'About Rani Kamlapati', href: '#about-rani-kamlapati' },
];

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const megaMenuTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = transparent && !scrolled;

  const handleMegaMouseEnter = () => {
    if (megaMenuTimerRef.current) clearTimeout(megaMenuTimerRef.current);
    setMegaMenuOpen(true);
  };

  const handleMegaMouseLeave = () => {
    megaMenuTimerRef.current = setTimeout(() => setMegaMenuOpen(false), 150);
  };

  return (
    <>
      {/* Main Navbar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isTransparent ? 'bg-transparent' : ''
        }`}
        style={{
          height: '52px',
          background: isTransparent ? 'transparent' : '#1A0A00',
          borderBottom: isTransparent ? 'none' : '1px solid rgba(201,168,76,0.25)',
          boxShadow: isTransparent ? 'none' : '0 2px 16px rgba(0,0,0,0.4)',
        }}
      >
        {/* Gold top accent line */}
        {!isTransparent && (
          <div style={{ height: '2px', background: 'linear-gradient(to right, #8B6914, #C9A84C, #E8C97A, #C9A84C, #8B6914)', position: 'absolute', top: 0, left: 0, right: 0 }} />
        )}
        <div className="flex items-center justify-between h-full px-4 md:px-6" style={{ paddingTop: isTransparent ? 0 : '2px' }}>
          {/* Left: Hamburger */}
          <button
            onClick={() => setMenuOpen(true)}
            className="flex items-center justify-center w-8 h-8 mr-3 flex-shrink-0"
            aria-label="Open menu"
          >
            <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
              <rect y="0" width="20" height="2" rx="1" fill={isTransparent ? 'white' : '#C9A84C'} />
              <rect y="7" width="20" height="2" rx="1" fill={isTransparent ? 'white' : '#C9A84C'} />
              <rect y="14" width="20" height="2" rx="1" fill={isTransparent ? 'white' : '#C9A84C'} />
            </svg>
          </button>

          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1.5">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', border: '1px solid rgba(201,168,76,0.5)' }}
              >
                {/* Crown SVG */}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M3 18h18M5 18l2-8 5 4 5-6 2 10" stroke="#FAF7F2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="5" cy="9" r="1.5" fill="#FAF7F2" />
                  <circle cx="12" cy="6" r="1.5" fill="#FAF7F2" />
                  <circle cx="19" cy="9" r="1.5" fill="#FAF7F2" />
                </svg>
              </div>
              <span
                style={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontWeight: 600,
                  fontSize: '17px',
                  letterSpacing: '0.3px',
                  color: isTransparent ? '#FFFFFF' : '#C9A84C',
                  transition: 'color 0.3s',
                }}
              >
                Rani Kamlapati
              </span>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <nav className="hidden lg:flex items-center gap-6 ml-8 flex-1">
            {['Events', 'Awards', 'Attractions', 'Exhibitors', 'About Rani Kamlapati'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-sm font-medium transition-colors duration-200"
                style={{
                  color: isTransparent ? 'rgba(255,255,255,0.9)' : 'rgba(232,201,122,0.85)',
                  fontFamily: 'Roboto, sans-serif',
                  letterSpacing: '0.5px',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = isTransparent ? '#FFFFFF' : '#C9A84C')}
                onMouseLeave={(e) => (e.currentTarget.style.color = isTransparent ? 'rgba(255,255,255,0.9)' : 'rgba(232,201,122,0.85)')}
              >
                {item}
              </a>
            ))}
          </nav>

          {/* Right: Search + CTA */}
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="flex items-center justify-center w-8 h-8"
              aria-label="Search"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <circle cx="8" cy="8" r="5.5" stroke={isTransparent ? 'white' : '#C9A84C'} strokeWidth="1.5" />
                <path d="M12.5 12.5L16 16" stroke={isTransparent ? 'white' : '#C9A84C'} strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <Link
              href="#join-us"
              className="hidden md:flex items-center px-3 h-8 font-medium text-xs tracking-wide transition-all duration-200 hover:opacity-90"
              style={{
                background: 'transparent',
                color: isTransparent ? 'white' : '#C9A84C',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 500,
                border: `1px solid ${isTransparent ? 'rgba(255,255,255,0.6)' : 'rgba(201,168,76,0.6)'}`,
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.5px',
              }}
            >
              Become Member
            </Link>
            <Link
              href="/login"
              className="flex items-center px-3 h-8 font-medium text-xs tracking-wide transition-all duration-200 hover:opacity-90"
              style={{
                background: 'linear-gradient(135deg, #C9A84C, #8B6914)',
                color: '#FAF7F2',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 500,
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.5px',
              }}
            >
              Sign In
            </Link>
            <Link
              href="#create-event"
              className="hidden md:flex items-center px-3 h-8 font-medium text-xs tracking-wide transition-all duration-200 hover:opacity-90"
              style={{
                background: 'transparent',
                color: '#C9A84C',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 500,
                border: '1px solid rgba(201,168,76,0.5)',
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.5px',
              }}
            >
              Store
            </Link>
            <Link
              href="/admin"
              className="hidden md:flex items-center gap-1.5 px-3 h-8 font-medium text-xs tracking-wide transition-all duration-200 hover:opacity-90"
              style={{
                background: 'rgba(201,168,76,0.12)',
                color: '#C9A84C',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 600,
                border: '1px solid rgba(201,168,76,0.35)',
                fontFamily: 'Roboto, sans-serif',
                letterSpacing: '0.5px',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Admin
            </Link>
          </div>
        </div>

        {/* Search Bar Dropdown */}
        {searchOpen && (
          <div className="absolute top-full left-0 right-0 shadow-lg px-4 py-3 animate-fadeIn" style={{ background: '#1A0A00', borderTop: '1px solid rgba(201,168,76,0.3)' }}>
            <div className="max-w-2xl mx-auto flex items-center gap-3">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0">
                <circle cx="8" cy="8" r="5.5" stroke="#C9A84C" strokeWidth="1.5" />
                <path d="M12.5 12.5L16 16" stroke="#C9A84C" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search events, venues, organizers..."
                className="flex-1 outline-none text-sm"
                style={{ background: 'transparent', color: '#FAF7F2', fontFamily: 'Roboto, sans-serif' }}
                autoFocus
              />
              <button onClick={() => setSearchOpen(false)} style={{ color: '#C9A84C' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Mega Menu Dropdown */}
      {megaMenuOpen && (
        <div
          className="fixed left-0 right-0 z-40 shadow-xl"
          style={{ top: '52px', background: '#1A0A00', borderTop: '1px solid rgba(201,168,76,0.3)', borderBottom: '2px solid rgba(201,168,76,0.4)' }}
          onMouseEnter={handleMegaMouseEnter}
          onMouseLeave={handleMegaMouseLeave}
        >
          <div className="max-w-[1280px] mx-auto px-6 py-8">
            <div className="grid grid-cols-4 gap-8">
              {megaMenuSections.map((section) => (
                <div key={section.title}>
                  <h4 className="text-xs font-bold uppercase tracking-widest mb-4" style={{ letterSpacing: '1.5px', color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>
                    {section.title}
                  </h4>
                  <ul className="space-y-2.5">
                    {section.items.map((item) => (
                      <li key={item.label}>
                        <a
                          href={item.href}
                          className="flex items-center gap-2 text-sm transition-colors duration-150"
                          style={{ color: 'rgba(250,247,242,0.75)', fontFamily: 'Roboto, sans-serif' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A84C')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(250,247,242,0.75)')}
                          onClick={() => setMegaMenuOpen(false)}
                        >
                          {item.label}
                          {item.badge && (
                            <span
                              className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                              style={{
                                background: item.badge === 'FREE' ? '#1A3A2A' : '#6B1A2A',
                                color: item.badge === 'FREE' ? '#4ADE80' : '#E8C97A',
                                border: `1px solid ${item.badge === 'FREE' ? 'rgba(74,222,128,0.3)' : 'rgba(201,168,76,0.3)'}`,
                              }}
                            >
                              {item.badge}
                            </span>
                          )}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Drawer */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex">
          {/* Overlay */}
          <div
            className="absolute inset-0"
            style={{ background: 'rgba(0,0,0,0.7)' }}
            onClick={() => setMenuOpen(false)}
          />
          {/* Drawer */}
          <div
            className="relative flex flex-col h-full overflow-y-auto animate-slideInLeft"
            style={{ width: '300px', background: '#1A0A00', borderRight: '1px solid rgba(201,168,76,0.3)' }}
          >
            {/* Gold top line */}
            <div style={{ height: '3px', background: 'linear-gradient(to right, #8B6914, #C9A84C, #E8C97A, #C9A84C, #8B6914)' }} />
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(201,168,76,0.2)' }}>
              <span style={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600, fontSize: '18px', color: '#C9A84C' }}>
                Rani Kamlapati
              </span>
              <button onClick={() => setMenuOpen(false)} style={{ color: '#C9A84C' }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            {/* Nav Items */}
            <nav className="flex-1 px-5 py-6">
              {mobileNavItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center py-3 text-sm font-medium transition-colors duration-200"
                  style={{ color: 'rgba(232,201,122,0.85)', borderBottom: '1px solid rgba(201,168,76,0.1)', fontFamily: 'Roboto, sans-serif', letterSpacing: '0.3px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#C9A84C')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(232,201,122,0.85)')}
                >
                  {item.label}
                </a>
              ))}
            </nav>
            {/* CTA */}
            <div className="px-5 pb-8 flex flex-col gap-3">
              <Link
                href="/login"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center h-11 font-medium text-sm transition-all duration-200 hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #C9A84C, #8B6914)', color: '#FAF7F2', borderRadius: '6px', fontFamily: 'Roboto, sans-serif', letterSpacing: '0.5px' }}
              >
                Sign In
              </Link>
              <Link
                href="#create-event"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center h-11 font-medium text-sm transition-all duration-200 hover:opacity-90"
                style={{ background: 'transparent', color: '#C9A84C', borderRadius: '6px', border: '1px solid rgba(201,168,76,0.5)', fontFamily: 'Roboto, sans-serif', letterSpacing: '0.5px' }}
              >
                Store
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
